/**
 * ChatScroller - WhatsApp Spacer Version
 *
 * - Renders at bottom with no visible scroll
 * - Perfect prepend behavior
 * - No scrollTop hacks
 * - No drift, no jump
 * - Uses your existing styles
 */

import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useImperativeHandle,
} from 'react';
import { IonGrid, IonList, IonRow, IonCol } from '@ionic/react';
import styles from '../../pages/Inbox.module.scss';

export interface ChatScrollerProps<T extends { id?: string }> {
  messages: T[];
  conversationId: string | number;
  loadOlder: () => Promise<void>;
  hasMore: boolean;
  renderMessage: (msg: T, index: number, previousMsg?: T) => React.ReactNode;
  containerClassName?: string;
  messageContainerClassName?: string;
  isLoadingOlder?: boolean;
  loadingSpinner?: React.ReactNode;
  titleCard?: React.ReactNode;
  maxVisibleMessages?: number;
  onScroll?: (scrollTop: number, scrollHeight: number, clientHeight?: number) => void;
  /** Extra px below the last message on top of the base spacer — used to clear a
   *  taller floating footer (e.g. the email channel's Subject row). */
  bottomSpacerExtra?: number;
  /** When the list can scroll by less than this many px in total, leave it at the top instead of
   *  scrolling to the bottom. A list that overflows only by the empty space kept for the floating
   *  footer would otherwise open shifted up a few pixels, clipping the top of the title card. */
  minScrollToPin?: number;
}

export const ChatScroller = React.forwardRef<
  { scrollToBottom: () => void; getScrollableRange: () => number },
  ChatScrollerProps<{ id?: string }>
>(({
  messages,
  conversationId,
  loadOlder,
  hasMore,
  renderMessage,
  containerClassName = '',
  messageContainerClassName = '',
  isLoadingOlder = false,
  loadingSpinner,
  titleCard,
  maxVisibleMessages = 500,
  onScroll,
  bottomSpacerExtra = 0,
  minScrollToPin = 0,
}, ref) => {

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const containerRef = useRef<any>(null);
  const spacerRef = useRef<HTMLDivElement>(null);

  const displayMessages = useMemo(() => {
    if (messages.length > maxVisibleMessages) return messages.slice(-maxVisibleMessages);
    return messages;
  }, [messages, maxVisibleMessages]);

  const readScrollMetrics = (el: HTMLElement) => {
    const maxTop = Math.max(0, el.scrollHeight - el.clientHeight);
    return {
      scrollTop: el.scrollTop,
      scrollHeight: el.scrollHeight,
      clientHeight: el.clientHeight,
      maxTop,
      deltaFromBottom: maxTop - el.scrollTop,
    };
  };

  const didInitialBottomScrollRef = useRef(false);
  const isProgrammaticScrollRef = useRef(false);
  const userNearBottomRef = useRef(true);
  const settleRafRef = useRef<number | null>(null);
  const settleUntilRef = useRef(0);
  const prevDisplayCountRef = useRef(0);
  const handleLoadOlderRef = useRef<typeof handleLoadOlder | null>(null);
  const rafScrollListenerRef = useRef<number | null>(null);
  const lastScrollTopRef = useRef(0);
  const lastLoadOlderTimeRef = useRef(0);
  const cleanupScrollListenerRef = useRef<(() => void) | null>(null);
  const lastRAFCheckTimeRef = useRef(0);
  const userHasScrolledRef = useRef(false);

  const scrollToBottom = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    const maxTop = Math.max(0, el.scrollHeight - el.clientHeight);
    isProgrammaticScrollRef.current = true;
    el.scrollTop = maxTop < minScrollToPin ? 0 : maxTop;
    requestAnimationFrame(() => {
      isProgrammaticScrollRef.current = false;
    });
  }, [minScrollToPin]);

  const startSettleToBottom = useCallback((reason: string, durationMs = 1200) => {
    settleUntilRef.current = Date.now() + durationMs;
    if (settleRafRef.current) {
      cancelAnimationFrame(settleRafRef.current);
      settleRafRef.current = null;
    }

    const tick = () => {
      if (Date.now() > settleUntilRef.current) {
        settleRafRef.current = null;
        return;
      }

      // Only pin if the user is at/near the bottom.
      if (userNearBottomRef.current) {
        scrollToBottom();
      }

      settleRafRef.current = requestAnimationFrame(tick);
    };

    settleRafRef.current = requestAnimationFrame(tick);
  }, [scrollToBottom]);

  // Expose scrollToBottom (rarely needed)
  useImperativeHandle(ref, () => ({
    scrollToBottom: () => {
      scrollToBottom();
    },
    // How many pixels the list can scroll in total (0 when everything fits on screen)
    getScrollableRange: () => {
      const el = containerRef.current as HTMLElement | null;
      return el ? Math.max(0, el.scrollHeight - el.clientHeight) : 0;
    },
  }), [scrollToBottom]);

  // Reset on conversation change (don't scroll here; we'll do it in layout before paint).
  useLayoutEffect(() => {
    didInitialBottomScrollRef.current = false;
    prevDisplayCountRef.current = 0;
    userNearBottomRef.current = true;
    userHasScrolledRef.current = false;
  }, [conversationId]);

  // Update handleLoadOlder ref (no dependency needed - it's always available from props)
  useEffect(() => {
    handleLoadOlderRef.current = handleLoadOlder;
  });

  // First bottom scroll should happen before paint (prevents "top then flash to bottom").
  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    if (didInitialBottomScrollRef.current) return;
    if (displayMessages.length === 0) return;

    didInitialBottomScrollRef.current = true;
    scrollToBottom();
    startSettleToBottom('initial.layout');
  }, [conversationId, displayMessages.length, scrollToBottom, startSettleToBottom]);

  // When new messages arrive and the user is already at the bottom, keep them pinned.
  useEffect(() => {
    const prev = prevDisplayCountRef.current;
    prevDisplayCountRef.current = displayMessages.length;
    if (!didInitialBottomScrollRef.current) return;
    if (displayMessages.length <= prev) return;
    if (!userNearBottomRef.current) return;

    scrollToBottom();
    startSettleToBottom('newMessages', 800);
  }, [displayMessages.length, scrollToBottom, startSettleToBottom]);

  // The bottom spacer grew/shrank (e.g. email Subject row toggled in the
  // footer) — re-pin to bottom if the user was already there so the last
  // message doesn't end up hidden behind the taller footer.
  useEffect(() => {
    if (!didInitialBottomScrollRef.current) return;
    if (!userNearBottomRef.current) return;
    scrollToBottom();
    startSettleToBottom('spacerResize', 400);
  }, [bottomSpacerExtra, scrollToBottom, startSettleToBottom]);

  // Preserve offset when loading older
  const handleLoadOlder = async () => {
    const el = containerRef.current;
    if (!el) return;

    const prevScrollHeight = el.scrollHeight;

    await loadOlder();

    // Wait a frame so DOM/layout updates are reflected.
    await new Promise<void>(resolve => requestAnimationFrame(() => resolve()));

    const newScrollHeight = el.scrollHeight;
    const diff = newScrollHeight - prevScrollHeight;

    el.scrollTop += diff; // perfect offset preservation
  };

  // RAF-based scroll listener for iOS (no polling, runs at 60fps)
  const setupRAFScrollListener = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;

    const checkScroll = () => {
      // A hidden page (display: none, e.g. after you leave the conversation) reports a zero height
      // and a reset scrollTop. That is not the user scrolling to the top, and treating it as such
      // fetched older messages from the server every time you navigated back.
      if (el.clientHeight === 0) {
        rafScrollListenerRef.current = requestAnimationFrame(checkScroll);
        return;
      }
      const now = Date.now();
      
      // Throttle RAF checks to every 30ms for faster iOS detection
      if (now - lastRAFCheckTimeRef.current >= 30) {
        lastRAFCheckTimeRef.current = now;
        
        // Read current scroll position
        const scrollTop = el.scrollTop;
        
        // Only update if scroll position actually changed
        if (scrollTop !== lastScrollTopRef.current) {
          lastScrollTopRef.current = scrollTop;
          userHasScrolledRef.current = true;

          // Update userNearBottomRef
          if (!isProgrammaticScrollRef.current) {
            const { deltaFromBottom } = readScrollMetrics(el);
            userNearBottomRef.current = deltaFromBottom <= 40;
          }

          // Load older messages when scrolled to top (threshold 150px - early detection)
          // NO debounce on first trigger, then 100ms after for rapid successive loads
          const timeSinceLastTrigger = now - lastLoadOlderTimeRef.current;
          if (scrollTop < 150 && hasMore && !isLoadingOlder && (lastLoadOlderTimeRef.current === 0 || timeSinceLastTrigger > 100) && handleLoadOlderRef.current) {
            lastLoadOlderTimeRef.current = now;
            handleLoadOlderRef.current().catch(err => console.error('RAF loadOlder error:', err));
          }

          // Call onScroll callback for header animation (only after user has scrolled to avoid flashing on load)
          if (onScroll && userHasScrolledRef.current) onScroll(scrollTop, el.scrollHeight, el.clientHeight);
        }
      }

      // Continue checking on next frame
      rafScrollListenerRef.current = requestAnimationFrame(checkScroll);
    };

    // Start the RAF loop
    rafScrollListenerRef.current = requestAnimationFrame(checkScroll);
  }, [hasMore, isLoadingOlder, onScroll]);

  const stopRAFScrollListener = () => {
    if (cleanupScrollListenerRef.current) {
      cleanupScrollListenerRef.current();
      cleanupScrollListenerRef.current = null;
    }
    if (rafScrollListenerRef.current) {
      cancelAnimationFrame(rafScrollListenerRef.current);
      rafScrollListenerRef.current = null;
    }
  };

  // Start RAF scroll listener on mount and cleanup on unmount
  useEffect(() => {
    setupRAFScrollListener();
    return () => {
      stopRAFScrollListener();
      if (settleRafRef.current) cancelAnimationFrame(settleRafRef.current);
      settleRafRef.current = null;
    };
  }, [hasMore, isLoadingOlder, onScroll, setupRAFScrollListener]);

  return (
    <IonGrid
      ref={containerRef}
      className={`${containerClassName} ${styles.chatScrollerContainer}`}
    >
      {/* WhatsApp-style spacer — only when there are older messages to load */}
      {hasMore && <div ref={spacerRef} className={styles.chatSpacer}></div>}

      {/* Title card */}
      {titleCard && (
        <div className={styles.chatScrollerTitleCard}>{titleCard}</div>
      )}

      {/* Loading older */}
      {isLoadingOlder && loadingSpinner && (
        <div className={styles.chatScrollerLoadingSpinner}>
          {loadingSpinner}
        </div>
      )}

      {/* Messages */}
      <IonList>
        {displayMessages.map((msg, index) => {
          const previousMsg = index > 0 ? displayMessages[index - 1] : undefined;
          return (
            <IonRow
              key={msg.id || `msg-${index}`}
              className={messageContainerClassName}
            >
              <IonCol>
                {renderMessage(msg, index, previousMsg)}
              </IonCol>
            </IonRow>
          );
        })}
      </IonList>
      <div
        className={styles.chatScrollerBottomSpacer}
        style={bottomSpacerExtra ? { height: `calc(160px + ${bottomSpacerExtra}px)` } : undefined}
      />
    </IonGrid>
  );
});

ChatScroller.displayName = 'ChatScroller';